/* jshint esversion: 6 */
/**
 * CopyContainerHelperService is a service class that handles operations related to copying and managing container data.
 * It integrates with various AngularJS services and utilities to perform clipboard operations, S3 uploads, and more.
 */
class CopyContainerHelperService {
    /**
     * Constructor initializes the service with necessary dependencies and sets up initial state variables.
     * @param {Object} $injector - AngularJS injector service.
     * @param {Object} $routeParams - AngularJS route parameters.
     * @param {Object} $http - AngularJS HTTP service.
     * @param {Object} $rootScope - AngularJS root scope.
     * @param {Object} $q - AngularJS promise service.
     * @param {Object} CryptoHelperService - Service for cryptographic operations.
     * @param {Object} CMSMediaUploadService - Service for media uploads.
     * @param {Object} $bridges - Service for bridge operations.
     * @param {Object} $timeout - AngularJS timeout service.
     * @param {Object} TaskDataService - Service for task data operations.
     * @param {Object} TaskSelectionService - Service for task selection operations.
     * @param {Object} $uibModal - AngularUI Bootstrap modal service.
     */
    constructor(
        $injector,
        $routeParams,
        $http,
        $rootScope,
        $q,
        CryptoHelperService,
        CMSMediaUploadService,
        $bridges,
        $timeout,
        TaskDataService,
        TaskSelectionService,
        $uibModal
    ) {
        // 'ngInject'; // Remove this line if present
        this.$injector = $injector;
        this.$routeParams = $routeParams;
        this.$http = $http;
        this.$rootScope = $rootScope;
        this.$q = $q;
        this.CryptoHelperService = CryptoHelperService;
        this.CMSMediaUploadService = CMSMediaUploadService;
        this.$bridges = $bridges;
        this.$timeout = $timeout;
        this.TaskDataService = TaskDataService;
        this.TaskSelectionService = TaskSelectionService;
        this.$uibModal = $uibModal;

        this.stringValue = null;
        this.s3UploadRunning = false;
        this.objectValue = { data: null };
        this.NotificationService = $injector.get('NotificationService');
        this.$signals = $injector.get('$signals');
        this.tempCount = 0;
        this.tempArr = [];
        this.copiedAfterDownload = false;
        this.mediaIsDownloaded = false;
        this.currentMedia = [];
        this.duplicateMediaIDs = [];

        this._Notification = null;
        this.taskContext = TaskDataService.fetchContext();
    }

    /**
     * Lazily initializes and returns the Notification service.
     * @returns {Object} Notification service instance.
     */
    Notification() {
        if (!this._Notification) {
            this._Notification = this.$injector.get('Notification');
        }
        return this._Notification;
    }

    /**
     * Commits changes to the store and emits a signal upon successful commit.
     * @param {Object} $scope - AngularJS scope.
     */
    commitChanges($scope) {
        let _defer = this.$q.defer();
        if (angular.isFunction(this.$bridges.data().commitStore)) {
            this.$bridges.data().commitStore((success_, err) => {
                if (success_) {
                    console.log('COMMIT SUCCESS');
                    this.$timeout(() => {
                        this.$signals.emit(IB.Signals.REPOSITORY_MEDIALIST_CHANGE);
                    }, 1000);
                    _defer.resolve();
                } else {
                    _defer.reject();
                }
            });
        } else {
            console.error('commitStore is not a function. Cannot commit changes!');
        }
    }

    /**
     * Deletes a single media object by UUID and optionally commits the changes.
     * @param {string} uuid - Unique identifier for the media object.
     * @param {boolean} doCommit - Flag to determine if changes should be committed.
     */
    deleteSingleMediaObject(uuid, doCommit) {
        this.$bridges.data().remove(uuid);
        if (doCommit) {
            this.commitChanges();
        }
    }

    /**
     * Clears the clipboard and optionally removes media from the media manager.
     * @param {Object} data - Data object.
     * @param {boolean} removeMedia - Flag to determine if media should be removed.
     * @param {boolean} showNotification - Flag to determine if a notification should be shown.
     */
    clearClipboard(data, removeMedia, showNotification) {
        navigator.clipboard.writeText(' ')
            .then(() => {
                console.log('Clear Clipboard success.');
                this.showContainer(data);
            })
            .catch((err) => {
                console.log('Something went wrong', err);
            });
        sessionStorage.removeItem('copyContainerData');
        sessionStorage.removeItem('copyContainerDataPresignedUrl');
        if (removeMedia && this.currentMedia && this.mediaIsDownloaded) {
            for (let currentMediaKey in this.currentMedia) {
                this.deleteSingleMediaObject(angular.copy(this.currentMedia[currentMediaKey].uuid), true);
            }
            if (showNotification) {
                new this.Notification()({
                    templateUrl: '/views/ib/notifications/copyContainerNotification/clearClipboard.html',
                    message: 'Removed all downloaded Media from the Media Manager.',
                    closeOnClick: false,
                    delay: 5000
                });
            }
        } else {
            if (showNotification) {
                new this.Notification()({
                    templateUrl: '/views/ib/notifications/copyContainerNotification/clearClipboard.html',
                    closeOnClick: false,
                    delay: 5000
                });
            }
        }
        this.mediaIsDownloaded = false;
    }

    /**
     * Emits a signal to remove media from the copy container.
     */
    removeMedia() {
        this.$signals.emit('onDeleteMediaCopyContainer');
    }

    /**
     * Checks if a string contains any of the substrings provided.
     * @param {string} str - The string to check.
     * @param {Array} substrings - Array of substrings to check against.
     * @returns {string|null} The found substring or null if none found.
     */
    containsAny(str, substrings) {
        for (let i = 0; i < substrings.length; i++) {
            let substring = substrings[i];
            if (str.indexOf(substring) !== -1) {
                return substring;
            }
        }
        return null;
    }

    /**
     * Decompresses an attachment if compression is activated.
     * @param {string} attachment - The attachment to decompress.
     * @returns {string} The decompressed attachment.
     */
    decompression(attachment) {
        if (!this.compressionActivate) return attachment;
        if (typeof attachment === "string" && attachment.indexOf(this.compressionKeyword) !== -1) {
            attachment = JSON.parse(pako.inflate(window.atob(attachment.replace(new RegExp(this.compressionKeyword, "g"), "")), this.compressionOptions));
        }
        return attachment;
    }

    /**
     * Compresses an attachment if compression is activated.
     * @param {string} attachment - The attachment to compress.
     * @returns {string} The compressed attachment.
     */
    compression(attachment) {
        if (!this.compressionActivate) return attachment;
        if (typeof attachment === "string" && attachment.indexOf(this.compressionKeyword) !== -1) {
            attachment = this.decompression(attachment);
        }
        try {
            attachment = this.compressionKeyword + window.btoa(pako.deflate(JSON.stringify(attachment), this.compressionOptions));
        } catch (err) {
            console.log(err);
        }
        return attachment;
    }

    /**
     * Replaces UUIDs in the data with new UUIDs.
     * @param {string} data - The data containing UUIDs to replace.
     * @param {Object} _thisRef - Reference to the current instance.
     * @param {Array} oldUUIdList - List of old UUIDs to replace.
     * @returns {string} The data with replaced UUIDs.
     */
    replaceUUID(data, _thisRef, oldUUIdList) {
        let indexFind = [];
        this.tempArr.findIndex((record) => {
            if (_thisRef.containsAny(data, record.oldUUID)) {
                indexFind.push(record.oldUUID);
            }
        });

        if (JSON.parse(data.split('::')[1]).attachment) {
            let blub = _thisRef.decompression(JSON.parse(data.split('::')[1]).attachment);
            let blubAttachment = JSON.parse(data.split('::')[1]).attachment;
            let blubAttachmentBG = _thisRef.decompression(JSON.parse(data.split('::')[1]).attachment).backgroundImage;
            if (blubAttachmentBG) {
                let indexFindBg = this.tempArr.findIndex((element) => {
                    return element.uuid !== undefined;
                });
                blub.backgroundImage = this.tempArr[indexFindBg].uuid;
                data = data.replaceAll(blubAttachment, _thisRef.compression(blub));
                this.copiedAfterDownload = true;
            }
        }

        if (indexFind.length !== 0) {
            for (let tempArrKey in this.tempArr) {
                data = data.replace(new RegExp(this.tempArr[tempArrKey].oldUUID, 'g'), this.tempArr[tempArrKey].uuid);
            }
            this.copiedAfterDownload = true;
        }
        return data;
    }

    /**
     * Reads UUID data from the clipboard.
     * @returns {string} The UUID data.
     */
    returnUUID() {
        return navigator.clipboard.readText()
            .then((text) => {
                console.log('UUID DATA ON COPY DATA===>', JSON.parse(this.CryptoHelperService.decrypt(text.split('::')[2])));
                let uuidDataNumber = JSON.parse(this.CryptoHelperService.decrypt(text.split('::')[2])).uuidDataNumber;
                console.log('Using existing UUID for Copy Container ==>', uuidDataNumber);
                return uuidDataNumber;
            })
            .catch((err) => {
                console.log('Read from clipboard ==> Something went wrong', err);
                let uuidDataNumber = sessionStorage.getItem('copyContainerData') ? JSON.parse(this.CryptoHelperService.decrypt(sessionStorage.copyContainerData.split('::')[2])).uuidDataNumber : crypto.randomUUID().toString();
                console.log('Generating a new UUID for Copy Container ==>', uuidDataNumber);
                return uuidDataNumber;
            });
    }

    /**
     * Copies data to the clipboard and handles various operations.
     * @param {Object} data - The data to copy.
     * @param {string} taskUUID - The task UUID.
     * @param {string} sheetUUID - The sheet UUID.
     * @param {string} containerKey - The container key.
     * @param {string} key - The key.
     * @param {string} presignedUrl - The presigned URL.
     */
    copyToClipboard(data, taskUUID, sheetUUID, containerKey, key, presignedUrl) {
        let _thisRef = this;
        let test = this.tempArr;
        this.copiedAfterDownload = false;
        let uuidDataNumber = null;
        let oldUUIdList = this.tempArr.map((el) => {
            return el.oldUUID;
        });
        console.log('OLD UUID LIST COPY TO CLIPBOARD ===>', oldUUIdList);
        let presignedUrlData = {
            'presignedUrl': presignedUrl === undefined ? 'undefined' : presignedUrl,
        };

        let presignedUrlDataSessionStorage = {
            'presignedUrl': 'undefined',
        };

        if (taskUUID === null) {
            for (let dataKey in data) {
                for (let dataIndex in data[dataKey]) {
                    data[dataKey][dataIndex] = _thisRef.replaceUUID(data[dataKey][dataIndex], _thisRef, oldUUIdList);
                }
            }
        }

        let uuidData = {
            'uuidDataNumber': uuidDataNumber === null ? crypto.randomUUID().toString() : uuidDataNumber,
        };

        let labelData = {
            'label': 'blub',
        };

        console.log('copied after download', this.copiedAfterDownload);
        if (this.copiedAfterDownload) {
            navigator.clipboard.readText()
                .then((text) => {
                    console.log('UUID DATA ON COPY DATA===>', JSON.parse(this.CryptoHelperService.decrypt(text.split('::')[2])));
                    uuidDataNumber = JSON.parse(this.CryptoHelperService.decrypt(text.split('::')[2])).uuidDataNumber;
                    console.log('Using existing UUID for Copy Container ==>', uuidDataNumber);
                    uuidData = {
                        'uuidDataNumber': uuidDataNumber === null ? crypto.randomUUID().toString() : uuidDataNumber,
                    };
                    sessionStorage.copyContainerData = this.CryptoHelperService.encrypt(angular.toJson(data)) + '::' + this.CryptoHelperService.encrypt(angular.toJson(presignedUrlDataSessionStorage)) + '::' + this.CryptoHelperService.encrypt(angular.toJson(uuidData));
                    sessionStorage.copyContainerUUID = this.CryptoHelperService.encrypt(angular.toJson(data)) + '::' + this.CryptoHelperService.encrypt(angular.toJson(presignedUrlDataSessionStorage)) + '::' + this.CryptoHelperService.encrypt(angular.toJson(uuidData));
                    console.log('UUID DATA IN STORAGE ===>', JSON.parse(this.CryptoHelperService.decrypt(sessionStorage.copyContainerData.split('::')[2])).uuidDataNumber);
                    _thisRef.reloadContainer(true, true);
                    _thisRef.addContainer(undefined, containerKey, key, data);
                })
                .catch((err) => {
                    console.log('Read from clipboard ==> Something went wrong', err);
                    console.log('PRESIGNED URL ==>', JSON.parse(this.CryptoHelperService.decrypt(sessionStorage.copyContainerData.split('::')[1])));
                    uuidDataNumber = crypto.randomUUID().toString();
                    console.log('Generating a new UUID for Copy Container ==>', uuidDataNumber);
                    uuidData = {
                        'uuidDataNumber': uuidDataNumber,
                    };
                    sessionStorage.copyContainerData = this.CryptoHelperService.encrypt(angular.toJson(data)) + '::' + this.CryptoHelperService.encrypt(angular.toJson(presignedUrlDataSessionStorage)) + '::' + this.CryptoHelperService.encrypt(angular.toJson(uuidData));
                    sessionStorage.copyContainerUUID = this.CryptoHelperService.encrypt(angular.toJson(data)) + '::' + this.CryptoHelperService.encrypt(angular.toJson(presignedUrlDataSessionStorage)) + '::' + this.CryptoHelperService.encrypt(angular.toJson(uuidData));
                    console.log('UUID DATA IN STORAGE ===>', JSON.parse(this.CryptoHelperService.decrypt(sessionStorage.copyContainerData.split('::')[2])).uuidDataNumber);
                    _thisRef.reloadContainer(true, true);
                    _thisRef.addContainer(undefined, containerKey, key, data);
                });
        } else {
            let copyContainerContent = this.CryptoHelperService.encrypt(angular.toJson(data)) + '::' + this.CryptoHelperService.encrypt(angular.toJson(presignedUrlData)) + '::' + this.CryptoHelperService.encrypt(angular.toJson(uuidData)) + '::' + this.CryptoHelperService.encrypt(angular.toJson(labelData));
            console.log('COPY CONTAINER CONTENT ==>', copyContainerContent);
            this.$signals.emit('removeOverlay');

            return this.$uibModal.open({
                templateUrl: "/views/ib/components/modal/getCopyContainerStringModal.html",
                windowClass: "delete-element-modal",
                backdrop: true,
                controller: function ($scope, $uibModalInstance) {
                    $scope.containerContentEncrypt = copyContainerContent;

                    $scope.onCancelBtnClick = function () {
                        $uibModalInstance.dismiss('cancel');
                    };

                    $scope.copyCode = function () {
                        navigator.clipboard.writeText(copyContainerContent)
                            .then(function () {
                                console.log('Wrote to Clipboard');
                                sessionStorage.copyContainerData = _thisRef.CryptoHelperService.encrypt(angular.toJson(data)) + '::' + _thisRef.CryptoHelperService.encrypt(angular.toJson(presignedUrlDataSessionStorage)) + '::' + _thisRef.CryptoHelperService.encrypt(angular.toJson(uuidData)) + '::' + _thisRef.CryptoHelperService.encrypt(angular.toJson(labelData));
                                if (taskUUID !== null || _thisRef.copiedAfterDownload) {
                                    _thisRef.showContainer();
                                    _thisRef.addContainer(undefined, containerKey, key, data);
                                }
                                $uibModalInstance.delete = true;
                                $uibModalInstance.dismiss('cancel');
                            })
                            .catch(function (err) {
                                console.log('Something went wrong', err);
                            });
                    };

                    $scope.onApplyBtnClick = function () {
                        $scope.copyCode();
                        $uibModalInstance.delete = true;
                        $uibModalInstance.dismiss('cancel');
                    };
                },
                scope: this.$rootScope.$new(),
                resolve: {
                    message: function () {
                        return 'test';
                    }
                }
            });
        }
    }

    srcToFile(src, fileName, mimeType) {
        return (fetch(src)
            .then((res) => {
                return res.arrayBuffer();
            })
            .then((buf) => {
                return new File([buf], fileName, { type: mimeType });
            })
        );
    }

    dataURItoBlob(dataURI) {
        let splitDataURI = dataURI.split(',');
        let byteString = splitDataURI[0].indexOf('base64') >= 0 ? atob(splitDataURI[1]) : decodeURI(splitDataURI[1]);
        let mimeString = splitDataURI[0].split(':')[1].split(';')[0];

        let ia = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }

        return new Blob([ia], { type: mimeString });
    }

    test() {
        let _defer = this.$q.defer();
        if (angular.isFunction(this.$bridges.data().commitStore)) {
            this.$bridges.data().commitStore((success_, data) => {
                if (success_) {
                    this.$timeout(() => {
                        this.$signals.emit(IB.Signals.REPOSITORY_MEDIALIST_CHANGE);
                        _defer.resolve();
                    }, 1000);
                } else {
                    _defer.reject();
                }
            });
        }
    }

    uploadRefresh(data, lastIteration, index, length, copiedMediaUUID, duplicate) {
        let uuid = data.uuid;
        let uploadName = data.uploadName.replace(/\.[^/.]+$/, "");
        delete data.uuid;
        let oldUUID = copiedMediaUUID ? copiedMediaUUID : uploadName;
        this.tempCount += 1;

        if (duplicate) {
            angular.forEach(duplicate, (data) => {
                this.tempArr.push({
                    uuid: data.uuid,
                    oldUUID: data.oldUUID,
                });
            });
        }

        this.tempArr.push({
            uuid: uuid,
            oldUUID: oldUUID,
        });

        if (this.tempCount === length) {
            this.copyToClipboard(this.temp, null);
        }

        this.taskContext.write(uuid, data);
        if (angular.isFunction(this.$bridges.data().put)) {
            this.$bridges.data().put(null, null, data);
        }
    }

    handleDuplicateMedia(uuidList) {
        console.log('DUPLICATE MEDIA ===>', JSON.parse(this.CryptoHelperService.decrypt(sessionStorage.copyContainerData.substring(0, sessionStorage.copyContainerData.indexOf('::')))));
        this.temp = JSON.parse(this.CryptoHelperService.decrypt(sessionStorage.copyContainerData.substring(0, sessionStorage.copyContainerData.indexOf('::'))));
        this.tempArr = uuidList;
        this.copyToClipboard(this.temp, null);
    }

    findIndexInArray(array, searchString) {
        var index = -1;

        for (var i = 0; i < array.length; i++) {
            if (array[i].includes(searchString)) {
                index = i;
                break;
            }
        }
        return index;
    }

    uploadMedia(taskUUID, response, copiedData) {
        var _thisRef = this;
        var _defer = this.$q.defer();

        function checkAndCallFunction(dup) {
            var uniqueDup = [];
            var seenUUIDs = {};

            for (var i = 0; i < dup.length; i++) {
                var currentUUID = dup[i].uuid;
                if (!seenUUIDs[currentUUID]) {
                    uniqueDup.push(dup[i]);
                    seenUUIDs[currentUUID] = true;
                }
            }

            return uniqueDup;
        }

        if (copiedData[0].length) {
            this.duplicateMediaIDs = checkAndCallFunction(this.duplicateMediaIDs);

            if (this.duplicateMediaIDs && this.duplicateMediaIDs.length > 0) {
                var filteredOld = response.data.fileNames.map(function(oldUUID, index) {
                    var shouldInclude = !_thisRef.duplicateMediaIDs.some(function(newItem) {
                        return newItem.oldUUID.split('.')[0] !== oldUUID.split('.')[0];
                    });
                    return shouldInclude ? { index: index, value: oldUUID } : null;
                }).filter(function(item) {
                    return item !== null;
                });
                console.log('FILTER RESPONSE DATA INDEX ==>', filteredOld);
                filteredOld.forEach(function(item) {
                    console.log('DELETE INDEX ==>', item);
                    var index = item.index;
                    response.data.fileNames.splice(index, 1);
                    response.data.files.splice(index, 1);
                    response.data.test.splice(index, 1);
                });
            }

            console.log('FILTER RESPONSE DATA FILES ==>', response);

            angular.forEach(response.data.files, function (data, i) {
                var index = _thisRef.findIndexInArray(copiedData[0], response.data.fileNames[i]);
                console.log('FIND INDEX ARRAY ===>', index);
                if (index >= 0) {
                    var imgBase64 = 'data:' + response.data.test[index] + ';base64,' + data;
                    var file1 = _thisRef.dataURItoBlob(imgBase64);
                    var dat = response.data.files;
                    var responseFileName = JSON.parse(copiedData[0][index].toString().split('::')[7])[i] !== undefined ? JSON.parse(copiedData[0][index].toString().split('::')[7])[i] : response.data.fileNames[i];
                    var fileOfBlob = new File([file1], responseFileName);
                    _thisRef.CMSMediaUploadService.add(fileOfBlob);
                    var formData1 = new FormData();
                    formData1.append("file", fileOfBlob);
                    var url = '/api/v1/';
                    _thisRef.$http({
                        url: url + 'entity/' + taskUUID + '/',
                        headers: {"Content-Type": undefined},
                        data: formData1,
                        method: "POST"
                    }).then(function (response) {
                        console.info('Upload finished, committing files now', response);
                        if (angular.isFunction(_thisRef.$bridges.data().commitStore)) {
                            _thisRef.$bridges.data().commitStore(function (success_, data) {
                                if (success_) {
                                    _thisRef.$timeout(function () {
                                        console.log('INDEX ===>', index);
                                        console.log('INDEX ===>', dat.length);
                                        _thisRef.currentMedia.push(response);
                                        index === dat.length - 1 ? _thisRef.uploadRefresh(response.data, true, index, dat.length, JSON.parse(copiedData[0][index].toString().split('::')[5])[0], checkAndCallFunction(_thisRef.duplicateMediaIDs)) : _thisRef.uploadRefresh(response.data, false, index, dat.length, JSON.parse(copiedData[0][index].toString().split('::')[5])[0]);
                                        _thisRef.test();
                                        _defer.resolve();
                                    }, 1000);
                                } else {
                                    _defer.reject();
                                }
                            });
                        }
                        return response;
                    }, function (response) {
                        return response;
                    });
                }
            });
            console.log('ONLY NEW FILES ===>', checkAndCallFunction(this.duplicateMediaIDs));
        }
        this.CMSMediaUploadService.clearList();
    }

    getDataFromS3(presignedUrl, taskUUID, sheetUUID, clipBoardContent) {
        console.log('presignedUrl ===>', presignedUrl);
        var _thisRef = this;

        var data = {
            presignedUrl: presignedUrl,
            taskUUID: taskUUID,
            sheetUUID: sheetUUID
        };

        this.$http.post(
            '/cms/copy-container-download/',
            data
        ).then(function (response) {
            console.log('RESPONSE ==>', response);
            navigator.clipboard.readText()
                .then(function (text) {
                    _thisRef.mediaIsDownloaded = true;
                    _thisRef.temp = _thisRef.CryptoHelperService.decrypt(text.substring(0, text.indexOf('::'))) ? JSON.parse(_thisRef.CryptoHelperService.decrypt(text.substring(0, text.indexOf('::')))) : [];
                    console.log('TEMP RESPONSE ==>', _thisRef.temp);
                    _thisRef.uploadMedia(taskUUID, response, _thisRef.temp);
                }).catch(function (err) {
                    console.log('Read from clipboard ==> Something went wrong', err);
                    console.log('Read from clipboard ==> No Copy Container Data in the Clipboard');
                    if (sessionStorage.getItem('copyContainerData') !== null) {
                        _thisRef.mediaIsDownloaded = true;
                        _thisRef.temp = _thisRef.CryptoHelperService.decrypt(sessionStorage.copyContainerData.substring(0, sessionStorage.copyContainerData.indexOf('::'))) ? JSON.parse(_thisRef.CryptoHelperService.decrypt(sessionStorage.copyContainerData.substring(0, sessionStorage.copyContainerData.indexOf('::')))) : [];
                        console.log('TEMP RESPONSE STORAGE==>', _thisRef.temp);
                        _thisRef.uploadMedia(taskUUID, response, _thisRef.temp);
                    }
                });
        });
    }

    copyContainer(jsonVar, containerKey, key, taskUUID, sheetUUID) {
        var jsonVar1 = [];

        if (this.s3UploadRunning) {
            this.Notification()({
                templateUrl: '/views/ib/notifications/copyContainerNotification/clearClipboard.html',
                title: key,
                closeOnClick: false,
                delay: false
            });
        } else {
            var mediaList = [];
            var that = this;
            navigator.clipboard.readText()
                .then(function (text) {
                    var temp = that.CryptoHelperService.decrypt(text.split('::')[0]) ? JSON.parse(that.CryptoHelperService.decrypt(text.split('::')[0])) : [];
                    var lastContainerNoMedia = false;
                    if (temp.length > 0 && temp.length <= 0 && sessionStorage.copyContainerData === undefined) {
                        temp.push(jsonVar);
                        console.log('TEMP DATA ==>', temp);
                        angular.forEach(temp, function (value, index) {
                            temp[index].forEach(function (value) {
                                console.log('MEDIA DATA TRUE/FALSE ===>', value.split('::')[4]);
                                console.log('MEDIA DATA ===>', temp[index]);
                                if (value.split('::')[4] !== 'false') {
                                    console.log('VALUE ==>', value.split('::')[4]);
                                    var media = value.split('::')[4];
                                    var mediaTest = JSON.parse(value.split('::')[4]);
                                    if (mediaTest.length) {
                                        for (var mediaTestKey in mediaTest) {
                                            console.log('MEDIA DATA NEW ==>', mediaTest[mediaTestKey]);
                                            mediaList.indexOf(value.split('::')[4]) === -1 || !value.split('::')[4] ? mediaList.push(mediaTest[mediaTestKey]) : console.log("This item already exists");
                                        }
                                    }
                                    console.log('VALUE SPLIT ==>', JSON.parse(value.split('::')[4]));
                                } else if (temp.length - 1 === index) {
                                    lastContainerNoMedia = true;
                                }
                            });
                        });
                        console.
console.log('DATA URL ===>', mediaList);
                        for (var mediaListKey in mediaList) {
                            var mediaListSplit = mediaList[mediaListKey].split('/');
                            console.log('MEDIA LIST ==>', mediaList[mediaListKey].split('/'));
                            console.log('MEDIA LIST Split ==>', mediaListSplit[2]);
                        }
                        that.sendMediaData(mediaList, taskUUID, sheetUUID, containerKey, key, temp, lastContainerNoMedia);
                    } else if (temp.length <= 0 && sessionStorage.copyContainerData === undefined) {
                        jsonVar1.push(jsonVar);
                        console.log('JSON VAR ==>', jsonVar1);

                        angular.forEach(jsonVar1, function (value, index) {
                            console.log('JSON VAR ==>', jsonVar1[index]);
                            jsonVar1[index].forEach(function (value) {
                                if (value.split('::')[4] !== 'false') {
                                    var media = JSON.parse(value.split('::')[4]);
                                    console.log('MEDIA ARR ==>', media);
                                    if (media.length) {
                                        media.forEach(function (data) {
                                            console.log('DATA NEW ===>>', data);
                                            if (mediaList.indexOf(data) === -1 && data) {
                                                mediaList.push(data);
                                            } else {
                                                console.log("This item already exists");
                                            }
                                        });
                                    }
                                    console.log('VALUE SPLIT ==>', JSON.parse(value.split('::')[4]));
                                }
                            });
                        });
                        console.log('DATA URL ===>', mediaList);
                        for (var i = 0; i < mediaList.length; i++) {
                            var mediaSplit = mediaList[i].split('/');
                            console.log('MEDIA LIST ==>', mediaList[i].split('/'));
                            console.log('MEDIA LIST Split ==>', mediaSplit[2] === that.TaskSelectionService.getTaskUUID());
                            if (mediaSplit[2] !== that.TaskSelectionService.getTaskUUID()) {
                                mediaList[i] = mediaList[i].replace(new RegExp(mediaSplit[2], 'g'), that.TaskSelectionService.getTaskUUID());
                            }
                        }
                        that.sendMediaData(mediaList, taskUUID, sheetUUID, containerKey, key, jsonVar1);
                    } else {
                        console.log('TOO MANY ITEMS');
                        that.overwriteCopyContainer(jsonVar, containerKey, key, taskUUID, sheetUUID);
                    }
                })
                .catch(function (err) {
                    console.log('Read from clipboard ==> Something went wrong', err);
                });
        }
    }

    removeFromClipboard(data, index) {
        var that = this;
        navigator.clipboard.readText()
            .then(function (text) {
                var temp = JSON.parse(that.CryptoHelperService.decrypt(text.substring(0, text.indexOf('::'))));
                var url = JSON.parse(that.CryptoHelperService.decrypt(text.split('::')[1]));
                temp.splice(index, 1);
                that.copyToClipboard(temp, undefined, undefined, undefined, undefined, url.presignedUrl);
            })
            .catch(function (err) {
                console.log('Read from clipboard ==> Something went wrong', err);
                var data = sessionStorage.copyContainerDataPresignedUrl ? sessionStorage.copyContainerDataPresignedUrl : sessionStorage.copyContainerData;
                var temp = JSON.parse(that.CryptoHelperService.decrypt(data.substring(0, data.indexOf('::'))));
                var url = JSON.parse(that.CryptoHelperService.decrypt(data.split('::')[1]));
                temp.splice(index, 1);
                that.copyToClipboard(temp, undefined, undefined, undefined, undefined, url.presignedUrl);
            });
    }

    overwriteCopyContainer(jsonVar, containerKey, key, taskUUID, sheetUUID) {
        let that = this;
        this.$signals.emit('removeOverlay');
        return this.$uibModal.open({
            templateUrl: "/views/ib/components/modal/overwriteCopyContainerModal.html",
            windowClass: "delete-element-modal",
            backdrop: true,
            controller: function ($scope, $uibModalInstance) {
                $scope.data = {};

                $scope.onCancelBtnClick = function () {
                    $uibModalInstance.dismiss('cancel');
                };

                $scope.onApplyBtnClick = function () {
                    $uibModalInstance.delete = true;
                    $uibModalInstance.dismiss('cancel');
                    that.clearClipboard(false);
                    that.copyContainer(jsonVar, containerKey, key, taskUUID, sheetUUID);
                    that.$signals.emit('applyOverlay');
                };
            },
            scope: this.$rootScope.$new(),
            resolve: {
                message: function () {
                    return 'test';
                }
            }
        });
    }

    editLabel(data, index, labelText) {
        let that = this;
        navigator.clipboard.readText()
            .then(function (text) {
                let temp = JSON.parse(that.CryptoHelperService.decrypt(text.substring(0, text.indexOf('::'))));
                let url = JSON.parse(that.CryptoHelperService.decrypt(text.split('::')[1]));
                let label = JSON.parse(temp[index][0].split('::')[6]).label;
                console.log('URL EDIT LABEL ==>', label);
                let abc = temp[index][0].replace(label, labelText);
                let blub = that.CryptoHelperService.decrypt(text.substring(0, text.indexOf('::'))).replace(angular.toJson(temp[index][0]), angular.toJson(abc));
                temp = JSON.parse(blub);
                that.copyToClipboard(temp, undefined, undefined, undefined, undefined, url.presignedUrl, 'test');
            })
            .catch(function (err) {
                console.log('Read from clipboard ==> Something went wrong', err);
                let data = sessionStorage.copyContainerDataPresignedUrl ? sessionStorage.copyContainerDataPresignedUrl : sessionStorage.copyContainerData;
                let temp = JSON.parse(that.CryptoHelperService.decrypt(data.substring(0, data.indexOf('::'))));
                let url = JSON.parse(that.CryptoHelperService.decrypt(data.split('::')[1]));
                let label = JSON.parse(temp[index][0].split('::')[6]).label;
                console.log('URL EDIT LABEL ==>', url);
                let abc = temp[index][0].replace(label, labelText);
                let blub = that.CryptoHelperService.decrypt(data.substring(0, data.indexOf('::'))).replace(angular.toJson(temp[index][0]), angular.toJson(abc));
                temp = JSON.parse(blub);
                that.copyToClipboard(temp, undefined, undefined, undefined, undefined, url.presignedUrl, 'test');
            });
    }

    showContainer(data) {
        this.$rootScope.$broadcast('showContainer', {
            data: data !== false
        });
        this.$rootScope.$broadcast('showContainer', {
            data: data !== false
        });
    }

    reloadContainer(data, reload) {
        this.$rootScope.$broadcast('reloadContainer', {
            data: data !== false,
            reload: reload ? reload : false
        });
    }

    addContainer(data, containerKey, key, temp) {
        console.log('TEMP ARRA ===>', temp);
        this.$rootScope.$broadcast('addContainer', {
            data: data !== false,
            containerKey: containerKey,
            key: key,
            length: temp.length
        });
    }

    sendMediaData(mediaData, taskUUID, sheetUUID, containerKey, key, tempData, lastContainerNoMedia) {
        let _thisRef = this;
        let _test = null;
        let data = {
            'mediaPathList[]': mediaData,
            'taskUUID': taskUUID,
            'sheetUUID': sheetUUID
        };
        console.log('MEDIA DATA FILTER ===>', mediaData);
        console.log('MEDIA DATA LAST CONTAINER NO MEDIA ===>', lastContainerNoMedia);

        if (!mediaData.length || lastContainerNoMedia) {
            this.copyToClipboard(tempData, taskUUID, sheetUUID, containerKey, key);
        } else {
            console.log('RESPONSE containerKey ==>', containerKey);
            console.log('RESPONSE containerKey ==>', containerKey.toString());
            console.log('RESPONSE tempData ==>', tempData);
            let notificationObject = this.Notification()({
                templateUrl: '/views/ib/notifications/copyContainerNotification/s3UploadInProgress.html',
                title: key,
                message: String(containerKey),
                closeOnClick: false,
                delay: false
            });

            notificationObject.then(function (notificationScope) {
                _test = notificationScope;
            });
            this.s3UploadRunning = true;
            this.$http.post(
                '/cms/copy-container/',
                data
            ).then(function (response) {
                console.log('RESPONSE ==>', response.data.success);
                if (response.data.success) {
                    _thisRef.copyToClipboard(tempData, taskUUID, sheetUUID, containerKey, key, response.data.message);
                    _test.kill();
                    _thisRef.s3UploadRunning = false;
                }
            }, function (response) {
                console.log('exception occured during uploading to s3 ', response);
                _thisRef.s3UploadRunning = false;
            });
        }
    }

    connect(data, key, onLoad, containerKey, mediaArr, duplicateMedia) {
        console.log('EVENT ==>', data);
        console.log('mediaArr ==>', mediaArr);
        this.currentMedia = mediaArr;
        this.duplicateMediaIDs = duplicateMedia;

        if (data && key !== true) {
            this.Notification()({
                templateUrl: '/views/ib/notifications/copyContainerNotification/copyCompleted.html',
                title: key,
                message: String(containerKey),
                closeOnClick: false,
                delay: 5000
            });
        }
        this.$rootScope.$broadcast('eventFired', {
            data: data ? data : false,
            key: key ? key : null
        });

        if (onLoad) {
            console.log('ON LOAD');
            this.Notification()({
                templateUrl: '/views/ib/notifications/copyContainerNotification/copyCompleted.html',
                title: key,
                closeOnClick: false,
                delay: 5000
            });
        }
    }
}

// Add explicit dependency injection
CopyContainerHelperService.$inject = [
    '$injector',
    '$routeParams',
    '$http',
    '$rootScope',
    '$q',
    'CryptoHelperService',
    'CMSMediaUploadService',
    '$bridges',
    '$timeout',
    'TaskDataService',
    'TaskSelectionService',
    '$uibModal'
];

export default CopyContainerHelperService;